import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, axios } from '../../context/AuthContext';
import { useVeliOgrenci } from '../../context/VeliOgrenciContext';
import VeliOgrenciSecici from '../../components/parent/VeliOgrenciSecici';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

/* ── Mobil Uyumluluk Stilleri ── */
const mobileStyles = `
  @media (max-width: 768px) {
    .grid-4 { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
    .grid-2 { grid-template-columns: 1fr !important; gap: 15px !important; }
    .welcome-banner { padding: 20px !important; flex-direction: column; text-align: center; }
    .welcome-banner div { display: flex; flexDirection: column; align-items: center; }
    .stat-card { padding: 15px !important; }
    .stat-value { font-size: 1.5rem !important; }
    
    /* Rehberlik Tablosu Mobil Görünüm */
    .table-container table, .table-container thead, .table-container tbody, .table-container th, .table-container td, .table-container tr { 
      display: block; 
    }
    .table-container thead tr { position: absolute; top: -9999px; left: -9999px; }
    .table-container tr { border: 1px solid var(--border); margin-bottom: 10px; border-radius: 12px; padding: 10px; background: var(--bg-card); }
    .table-container td { border: none; position: relative; padding-left: 45% !important; text-align: right !important; min-height: 34px; display: flex; align-items: center; justify-content: flex-end; }
    .table-container td:before { 
      content: attr(data-label); position: absolute; left: 10px; width: 40%; padding-right: 10px; 
      white-space: nowrap; text-align: left; font-weight: 700; color: var(--text-secondary); font-size: 0.75rem;
    }
    
    .chart-container-mobile { height: 200px !important; }
    .action-buttons-mobile { grid-template-columns: 1fr !important; }
  }
`;

/* ── Shared Tooltip ── */
const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: '0.82rem', color: p.color, fontWeight: 700 }}>{p.name}: {p.value}</div>
      ))}
    </div>
  );
};

/* ══════════════════════════════════════
   VELİ DASHBOARD
══════════════════════════════════════ */
export function VeliDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { seciliOgrenci, loading: contextLoading } = useVeliOgrenci();
  const [ogrenci, setOgrenci] = useState(null);
  const [sonuclar, setSonuclar] = useState(null);
  const [ogrenciDetay, setOgrenciDetay] = useState(null);
  const [bildirimler, setBildirimler] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        let ogr = seciliOgrenci;
        if (!ogr) {
          const ogrRes = await axios.get('/ogrenciler?limit=10');
          const list = ogrRes.data.data || [];
          ogr = list[0];
        }
        if (!ogr) { setLoading(false); return; }
        setOgrenci(ogr);

        const [detayRes, sonucRes, genelSonucRes, bilRes] = await Promise.all([
          axios.get(`/ogrenciler/${ogr.id}`),
          axios.get(`/denemeler/ogrenci/${ogr.id}`),
          axios.get('/turkiye-geneli-deneme-sonuclari/benim').catch(() => null),
          axios.get('/bildirimler?limit=5'),
        ]);
        if (detayRes.data.success) setOgrenciDetay(detayRes.data.data);
        const normalSonuclar = sonucRes.data.success ? (sonucRes.data.data?.sonuclar || []) : [];
        const detaySonuclari = detayRes.data.success ? (detayRes.data.data?.son_denemeler || []) : [];
        const allGenel = genelSonucRes?.data?.success ? (genelSonucRes.data.data?.sonuclar || []) : [];
        const genelSonuclar = allGenel.filter((s) => Number(s.ogrenci_id) === Number(ogr.id));
        const siraliSonuclar = [
          ...(normalSonuclar.length ? normalSonuclar : detaySonuclari),
          ...genelSonuclar,
        ].map((sonuc) => ({
          ...sonuc,
          tarih: sonuc.tarih || sonuc.sinav_tarihi || sonuc.created_at || null,
        })).sort((a, b) => {
          const tarihA = new Date(a.tarih || 0).getTime();
          const tarihB = new Date(b.tarih || 0).getTime();
          return (Number.isFinite(tarihA) ? tarihA : 0) - (Number.isFinite(tarihB) ? tarihB : 0);
        });
        setSonuclar({ sonuclar: siraliSonuclar });
        if (bilRes.data.success) setBildirimler(bilRes.data.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    if (!contextLoading) {
      load();
    }
  }, [seciliOgrenci, contextLoading]);

  if (loading) return <div className="page-loader"><div className="spinner" style={{ width: 36, height: 36 }} /></div>;

  const sonucList = sonuclar?.sonuclar?.length ? sonuclar.sonuclar : (ogrenciDetay?.son_denemeler || []);
  const sonSonuc = sonucList[sonucList.length - 1];
  const hedefNet = parseFloat(ogrenciDetay?.hedef_net || ogrenci?.hedef_net || 0);
  const sonNet = parseFloat(sonSonuc?.toplam_net || 0);
  const pct = hedefNet > 0 ? Math.min((sonNet / hedefNet) * 100, 100) : 0;
  const color = pct >= 100 ? '#27AE60' : pct >= 70 ? '#D4853A' : '#C0392B';
  const devData = ogrenciDetay?.devamsizlik || {};
  const toplamDers = parseInt(devData.toplam_ders || devData.toplam || 0, 10);
  const devamsiz = parseInt(devData.devamsiz || 0, 10);
  const mevcut = parseInt(devData.mevcut || 0, 10);
  const katilimPct = toplamDers > 0 ? Math.round((mevcut / toplamDers) * 100) : 100;

  const chartData = sonucList.map(s => ({
    name: s.tarih ? new Date(s.tarih).toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' }) : '-',
    Net: parseFloat(s.toplam_net || 0).toFixed(1),
    Hedef: hedefNet,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <style>{mobileStyles}</style>

      {/* Birden fazla öğrenci varsa her sayfada en üstte kartlar görünür */}
      <VeliOgrenciSecici />

      <div className="welcome-banner animate-fadeInDown">
        <div>
          <div style={{ fontSize: '0.72rem', color: 'var(--accent-light)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 6 }}>Veli Paneli</div>
          <h1 style={{ fontSize: '1.8rem', marginBottom: 6 }}>Merhaba, {user?.ad}</h1>
          {ogrenci && <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <span className="badge badge-info">{ogrenci.ad} {ogrenci.soyad}</span>
            {ogrenci.sinif_adi && <span className="badge badge-muted">{ogrenci.sinif_adi}</span>}
          </div>}
        </div>
      </div>

      <div className="grid grid-4 stagger">
        {[
          { label: 'Son Net',       value: sonNet > 0 ? parseFloat(sonNet||0).toFixed(1) : '—', icon: '▦', color },
          { label: 'Hedef Net',     value: hedefNet || '—',     icon: '◎', color: '#5B8DD4' },
          { label: 'İlerleme',      value: `%${pct.toFixed(0)}`, icon: '◈', color },
          { label: 'Devamsızlık',   value: toplamDers > 0 ? `${devamsiz}/${toplamDers}` : '—', icon: '◉', color: katilimPct >= 85 ? '#10B981' : '#F97316' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ '--stat-color': s.color }}>
            <div className="stat-icon">{s.icon}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-2">
        <div className="card animate-fadeInUp">
          <h3 style={{ fontSize: '1rem', marginBottom: 16 }}>Deneme Trendi</h3>
          <div className="chart-container-mobile" style={{ height: 180 }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="veliGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" dataKey="Net" stroke={color} strokeWidth={2} fill="url(#veliGrad)" dot={{ fill: color, r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Henüz veri yok</div>}
          </div>
        </div>

        <div className="card animate-fadeInUp" style={{ animationDelay: '150ms' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: '1rem' }}>Bildirimler</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/veli/bildirimler')}>Tümü →</button>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
            padding: '10px 12px',
            background: 'var(--bg-elevated)',
            borderRadius: 'var(--r-md)',
            border: '1px solid var(--border)'
          }}>
            <div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Katılım Oranı</div>
              <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 800, fontSize: '1rem', color: katilimPct >= 85 ? '#10B981' : '#F97316' }}>
                %{katilimPct}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Devamsız Ders</div>
              <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 800, fontSize: '1rem', color: devamsiz > 0 ? '#EF4444' : '#10B981' }}>
                {devamsiz}
              </div>
            </div>
          </div>
          {bildirimler.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Yeni bildirim yok</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {bildirimler.map((b, i) => (
                <div key={i} style={{
                  padding: '10px 12px',
                  background: b.okundu ? 'var(--bg-elevated)' : 'rgba(192,57,43,0.06)',
                  border: `1px solid ${b.okundu ? 'var(--border)' : 'var(--accent-border)'}`,
                  borderRadius: 'var(--r-md)',
                  opacity: 0, animation: `fadeInUp 0.3s ease ${i * 60 + 200}ms both`,
                }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 2 }}>{b.baslik}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{b.mesaj?.slice(0, 70)}{b.mesaj?.length > 70 ? '...' : ''}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-2 action-buttons-mobile" style={{ gap: 12 }}>
        <button className="btn btn-outline" style={{ padding: '16px', justifyContent: 'center', fontSize: '0.9rem' }} onClick={() => navigate('/veli/basari')}>
          ◈ Başarı Detayı →
        </button>
        <button className="btn btn-outline" style={{ padding: '16px', justifyContent: 'center', fontSize: '0.9rem' }} onClick={() => navigate('/veli/devamsizlik')}>
          ◉ Devamsızlık →
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   REHBER DASHBOARD
══════════════════════════════════════ */
export function RehberDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ogrenciler, setOgrenciler] = useState([]);
  const [etutTalep, setEtutTalep] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get('/ogrenciler?limit=30'),
      axios.get('/rehberlik/etut-talepleri'),
    ]).then(([o, e]) => {
      if (o.data.success) setOgrenciler(o.data.data);
      if (e.data.success) setEtutTalep(e.data.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page-loader"><div className="spinner" style={{ width: 36, height: 36 }} /></div>;

  const bekleyen = etutTalep.filter(e => e.durum === 'bekliyor').length;

  const LINKS = [
    { label: 'Görüşmeler',     icon: '◉', path: '/rehber/gorusmeler',  color: 'var(--accent)' },
    { label: 'Konular',        icon: '📚', path: '/rehber/konular',     color: '#D4853A' },
    { label: 'Tekrarlar',      icon: '🔁', path: '/rehber/tekrarlar',   color: '#F97316' },
    { label: 'Çalışma Prog.',  icon: '▦', path: '/rehber/programlar',  color: '#5B8DD4' },
    { label: 'Etüt Talepleri', icon: '▨', path: '/rehber/etutler',     color: '#27AE60', badge: bekleyen },
    { label: 'Randevular',     icon: '▤', path: '/rehber/randevular',  color: '#9B72CF' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <style>{mobileStyles}</style>
      <div className="welcome-banner animate-fadeInDown">
        <div>
          <div style={{ fontSize: '0.72rem', color: 'var(--accent-light)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 6 }}>Rehber Öğretmen</div>
          <h1 style={{ fontSize: '1.8rem', marginBottom: 4 }}>Merhaba, {user?.ad}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{ogrenciler.length} öğrenci takibinizde</p>
        </div>
        {bekleyen > 0 && (
          <div style={{ background: 'rgba(192,57,43,0.1)', border: '1px solid var(--accent-border)', borderRadius: 'var(--r-md)', padding: '10px 16px', display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: '1rem' }}>⚠</span>
            <span style={{ fontSize: '0.85rem', color: 'var(--accent-light)', fontWeight: 600 }}>{bekleyen} bekleyen talep</span>
          </div>
        )}
      </div>

      <div className="grid grid-4 stagger">
        {LINKS.map((q, i) => (
          <button key={i} onClick={() => navigate(q.path)} style={{
            background: `${q.color}10`, border: `1px solid ${q.color}30`,
            borderRadius: 'var(--r-lg)', padding: '20px', cursor: 'pointer',
            transition: 'all 0.2s', textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: 12, position: 'relative',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = `${q.color}60`; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = `${q.color}30`; }}
          >
            {q.badge > 0 && (
              <div style={{ position: 'absolute', top: 10, right: 10, background: 'var(--accent)', color: 'white', fontSize: '0.65rem', fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--r-full)' }}>{q.badge}</div>
            )}
            <div style={{ width: 40, height: 40, borderRadius: 'var(--r-md)', background: `${q.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', color: q.color }}>{q.icon}</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: 2 }}>{q.label}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Görüntüle →</div>
            </div>
          </button>
        ))}
      </div>

      <div className="card animate-fadeInUp" style={{ animationDelay: '200ms', padding: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 16px 10px' }}>
          <h3 style={{ fontSize: '1rem' }}>Öğrenci Listesi</h3>
          <span className="badge badge-muted">{ogrenciler.length}</span>
        </div>
        {ogrenciler.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Öğrenci yok</div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Öğrenci</th>
                  <th>Sınıf</th>
                  <th>Sınav Türü</th>
                  <th>Hedef Net</th>
                </tr>
              </thead>
              <tbody>
                {ogrenciler.slice(0, 8).map((o, i) => (
                  <tr key={i} style={{ opacity: 0, animation: `fadeInUp 0.3s ease ${i * 50 + 300}ms both` }}>
                    <td data-label="Öğrenci"><span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{o.ad} {o.soyad}</span></td>
                    <td data-label="Sınıf"><span className="badge badge-info">{o.sinif_adi || '—'}</span></td>
                    <td data-label="Sınav Türü"><span className="badge badge-muted">{o.sinav_turu || '—'}</span></td>
                    <td data-label="Hedef Net"><span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, color: 'var(--accent-light)' }}>{o.hedef_net || '—'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
